import {
  createApi,
  fetchBaseQuery,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import i18n from "i18n";
import { logout, setAccessToken } from "reduxFiles/slices/authSlice";
import { RootState } from "reduxFiles/store";
import { addNotification } from "../slices/notificationSlice";

export const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";

const defaultMeta = {
  showSuccess: true,
  showError: true,
};

const rawBaseQuery = fetchBaseQuery({
  baseUrl: API_URL,
  prepareHeaders: (headers, { getState }) => {
    const { accessToken } = (getState() as RootState).auth;
    if (accessToken) {
      headers.set("Authorization", `Bearer ${accessToken}`);
    }
    const currentLanguage = i18n.language || "fa";
    headers.set("x-lang", currentLanguage);
    return headers;
  },
});

const baseQueryWithReauth = async (args: any, api: any, extraOptions: any) => {
  const requestMeta =
    typeof args === "object" && "meta" in args ? args.meta : {};
  const meta = {
    ...defaultMeta,
    ...requestMeta,
  };

  let result = await rawBaseQuery(args, api, extraOptions);

  // refresh token
  let isRefreshing = false;
  let refreshPromise: Promise<any> | null = null;

  if (result.error && (result.error as FetchBaseQueryError).status === 401) {
    const isRefreshCall =
      typeof args === "object" && args.url === "/auth/refresh-token";

    const { refreshToken } = (api.getState() as RootState).auth;

    if (isRefreshCall) {
      api.dispatch(logout());
      return result;
    }

    if (refreshToken) {
      try {
        if (!isRefreshing) {
          isRefreshing = true;

          refreshPromise = Promise.resolve(
            rawBaseQuery(
              {
                url: "/auth/refresh-token",
                method: "POST",
                body: { refreshToken },
              },
              api,
              extraOptions
            )
          ).finally(() => {
            isRefreshing = false;
          });
        }

        const refreshResult = await refreshPromise;

        if (refreshResult?.data) {
          const { accessToken: newAccessToken, refreshToken: newRefreshToken } =
            refreshResult.data as any;

          api.dispatch(setAccessToken(newAccessToken));
          sessionStorage.setItem("accessToken", newAccessToken);

          if (newRefreshToken) {
            sessionStorage.setItem("refreshToken", newRefreshToken);
          }

          result = await rawBaseQuery(args, api, extraOptions);
        } else {
          api.dispatch(logout());
        }
      } catch {
        api.dispatch(logout());
      }
    } else {
      api.dispatch(logout());
    }
  }

  // error notif
  if (result.error && meta.showError) {
    const error: any = result.error;

    let message = i18n.t("api.defaultError");
    let title;

    if (Array.isArray(error?.data?.message)) {
      message = error.data.message.join("\n");

      title =
        (error.data.error || "Error") +
        (error.data.statusCode ? ` (${error.data.statusCode})` : "");
    } else if (error?.data?.message) {
      message = error.data.message;

      title =
        (error.data.error || "Error") +
        (error.data.statusCode ? ` (${error.data.statusCode})` : "");
    } else if (Array.isArray(error?.data?.errors)) {
      message = error.data.errors.join("\n");
    } else if (error?.status === "FETCH_ERROR") {
      message = i18n.t("api.networkError");
    }

    api.dispatch(
      addNotification({
        message,
        title,
        type: "error",
      })
    );
  }

  // success notif
  if (result.data && meta.showSuccess) {
    const data: any = result.data;

    if (data?.message) {
      api.dispatch(
        addNotification({
          message: data.message,
          title: i18n.t("api.successTitle"),
          type: "success",
        })
      );
    }
  }

  return result;
};

export const baseApi = createApi({
  reducerPath: "baseApi",
  baseQuery: baseQueryWithReauth,
  endpoints: () => ({}),
  tagTypes: [
    "Auth",
    "ResponderAuth",
    "Profile",
    "Users",
    "Responders",
    "Provinces",
    "Tags",
    "Groups",
    "Criterias",
    "Industries",
    "Organizations",
    "UserOrganizations",
    "Companies",
    "ResponderProfile",
    "ResponderAssessment",
    "Assessments",
    "Licenses",
    "Transactions",
    "Settings",
    "CompanyVerification",
    "AdminDashboard",
    "AdminDashboardOverview",
    "AdminAverageOhi",
    "AdminRankingOhi",
    "AdminAssessmentSales",
    "AdminAssessmentExport",
    "AdminAssessmentCapacityUtilization",
    "AdminAssessmentTechnologicSales",
    "AdminAssessmentChart",
    "AdminLicenseChart",
    "OrganizationDashboard",
    "Forms",
    "OrganizationAssessments",
    "OrganizationResponding",
    "AdminOrganizationAssessments",
    "Major",
    "OrganizationLicensePlans",
    "LicensePlans",
    "Plans",
    "Requests",
  ],
});
